import { Component } from '@theme/component';

/**
 * Shows a tooltip with the full product title when hovering or focusing a
 * product card title that is visually truncated with an ellipsis.
 *
 * Works via event delegation so it supports cards rendered dynamically by
 * third-party scripts (e.g. the SearchSpring collection grid).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} bubble - The tooltip bubble element.
 *
 * @extends Component<Refs>
 */
class ProductTitleTooltip extends Component {
  requiredRefs = ['bubble'];

  #controller = new AbortController();

  /** @type {Element | null} */
  #activeTitle = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;

    document.addEventListener('pointerover', this.#handlePointerOver, { signal });
    document.addEventListener('focusin', this.#handleFocusIn, { signal });
    window.addEventListener('scroll', this.#hide, { signal });
    window.addEventListener('resize', this.#hide, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * The selector matching the product card title elements to watch.
   */
  get #titleSelector() {
    return this.getAttribute('data-titles') || '.ss__result__name';
  }

  /**
   * @param {PointerEvent} event - The pointerover event.
   */
  #handlePointerOver = (event) => {
    if (!matchMedia('(hover: hover)').matches) return;

    const title = event.target instanceof Element ? event.target.closest(this.#titleSelector) : null;

    if (!title || title === this.#activeTitle) return;

    this.#show(title);
    title.addEventListener('pointerleave', this.#hide, { once: true });
  };

  /**
   * @param {FocusEvent} event - The focusin event.
   */
  #handleFocusIn = (event) => {
    const title = event.target instanceof Element ? event.target.closest(this.#titleSelector) : null;

    if (!title || title === this.#activeTitle) return;

    this.#show(title);
    event.target.addEventListener('focusout', this.#hide, { once: true });
  };

  /**
   * Whether the title's text is visually truncated (line clamp or ellipsis).
   *
   * @param {Element} title - The title element.
   */
  #isTruncated(title) {
    return title.scrollHeight > title.clientHeight + 1 || title.scrollWidth > title.clientWidth + 1;
  }

  /**
   * Shows the tooltip above the title, clamped to the viewport edges.
   *
   * @param {Element} title - The title element to describe.
   */
  #show(title) {
    if (!this.#isTruncated(title)) return;

    const { bubble } = this.refs;
    const text = title.textContent?.trim();

    if (!text) return;

    bubble.textContent = text;
    this.setAttribute('visible', '');
    this.#activeTitle = title;

    const rect = title.getBoundingClientRect();
    const viewportMargin = 8;
    const halfWidth = bubble.offsetWidth / 2;
    const centerX = rect.left + rect.width / 2;
    const x = Math.min(Math.max(centerX, viewportMargin + halfWidth), window.innerWidth - viewportMargin - halfWidth);

    bubble.style.top = `${rect.top}px`;
    bubble.style.left = `${x}px`;
  }

  #hide = () => {
    if (!this.#activeTitle) return;

    this.#activeTitle = null;
    this.removeAttribute('visible');
  };
}

if (!customElements.get('product-title-tooltip')) {
  customElements.define('product-title-tooltip', ProductTitleTooltip);
}
